import '../classe/classe.scss';
import React, { useEffect, useRef, useState } from 'react';
import { Link, RouteComponentProps } from 'react-router-dom';
import { Breadcrumb, Tabs, Grid, message, Tooltip } from 'antd';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { getEcole } from '../ecole/ecole.reducer';
import { getElevesByClasse } from '../eleve/eleve.reducer';
import { deleteCalendar, deleteCalendarEntry, getCalendar, updateCalendarEntry } from '../calendar/calendarEntry.reducer';
import { getSortState } from 'react-jhipster';
import { getSession } from 'app/shared/reducers/authentication';
import { ITEMS_PER_PAGE } from 'app/shared/util/pagination.constants';
import { hasAnyAuthority } from 'app/shared/auth/private-route';
import { AUTHORITIES } from 'app/config/constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import moment from 'moment';
import 'moment/locale/fr';
import { faClone } from '@fortawesome/free-solid-svg-icons';
import { useHasAnyAuthority } from 'app/hooks/useHasAnyAuthority';
import { getClasse } from '../classe/classe.reducer';
import ClasseCalendar from '../classe/ClasseCalendar';
import ClasseEvaluations from '../classe/ClasseEvaluations';

moment.locale('fr');

const { useBreakpoint } = Grid;
const normalizeTabKey = (tab: string | null) => (tab === '2' || tab === '3' ? '2' : '1');

export const Calendar = (props: RouteComponentProps<{ ecoleid: string; anneeScolaireId: string; classeid: string }>) => {
  const dispatch = useAppDispatch();
  const { TabPane } = Tabs;
  const isNotComptable = !useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.COMPTABLE]));
  const isNotSurveillantGeneral = !useAppSelector(state =>
    hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.SURVEILLANT_GENERAL])
  );
  const isAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.ADMIN]));
  const requiredAuthorities = [AUTHORITIES.ADMIN, AUTHORITIES.DIRECTEUR, AUTHORITIES.DIRECTEUR_ETUDE];
  const hasPermission = useHasAnyAuthority(requiredAuthorities);
  const isProf = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.PROFESSEUR]));
  const currentEcole = useAppSelector(state => state.ecole.entity);
  const classe = useAppSelector(state => state.classe.entity);
  const listCalendarEntry = useAppSelector(state => state.calendarEntry.entities);
  const loadingCalendar = useAppSelector(state => state.calendarEntry.loading);
  const activeAnneScolaire = useAppSelector(state => state.anneeScolaire.entityActive);

  const [visibleModal, setVisibleModal] = useState(false);
  const [calendarEntryId, setCalendarEntryId] = useState('');
  const [visibleResetModal, setVisibleResetModal] = useState(false);
  const [activeTab, setActiveTab] = useState('1');
  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getSortState(props.location, ITEMS_PER_PAGE, 'id'), props.location.search)
  );

  const clickTimeoutRef = useRef(null);

  const ecoleid = props.match.params.ecoleid;
  const classeid = props.match.params.classeid;
  const anneeScolaireId = props.match.params.anneeScolaireId;

  useEffect(() => {
    dispatch(getSession());
    dispatch(getClasse(classeid));
    dispatch(getEcole(ecoleid));
  }, [classeid, ecoleid]);

  useEffect(() => {
    if (activeTab !== '1') return;
    const entity = {
      classeid,
      anneeScolaireId,
    };
    dispatch(getCalendar(entity));
  }, [activeTab, classeid, anneeScolaireId, paginationState.activePage]);

  useEffect(() => {
    const params = new URLSearchParams(props.location.search || '');
    const tab = params.get('tab');
    const nextTab = normalizeTabKey(tab);

    if (nextTab !== activeTab) {
      setActiveTab(nextTab);
    }

    if (tab && tab !== nextTab) {
      params.set('tab', nextTab);
      props.history.replace({ search: params.toString() });
    }
  }, [activeTab, props.history, props.location.search]);

  const eleveListe = () => {
    const entity = {
      ecoleId: ecoleid,
      classeId: classeid,
      page: paginationState.activePage - 1,
      size: paginationState.itemsPerPage,
    };
    dispatch(getElevesByClasse(entity));
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleModal = () => {
    props.history.push(`/calendar/${ecoleid}/${anneeScolaireId}/${classeid}/calendarEntry/new`);
  };

  const onSelect = (eventId: string) => {
    props.history.push(`/calendar/${ecoleid}/${anneeScolaireId}/${classeid}/calendarEntry/${eventId}/update`);
  };

  const onDuplicate = (eventId: string) => {
    props.history.push(`/calendar/${ecoleid}/${anneeScolaireId}/${classeid}/calendarEntry/${eventId}/duplicate`);
  };

  const onDelete = () => {
    setVisibleModal(false);
    const entity = { id: calendarEntryId, classeId: classeid };
    dispatch(deleteCalendarEntry(entity));
  };

  const resetCalendar = (classeId: string) => {
    setVisibleResetModal(false);
    dispatch(deleteCalendar({ classeId, anneeScolaireId }));
  };

  function isLessThanAnHour(heureDebut, heureFin) {
    const startTime = moment(heureDebut, 'HH:mm');
    const endTime = moment(heureFin, 'HH:mm');

    const minutesDifference = endTime.diff(startTime, 'minutes');

    return minutesDifference < 60;
  }

  const events = Array.isArray(listCalendarEntry)
    ? listCalendarEntry.map(event => {
        const referenceMonday = moment().startOf('isoWeek');
        const dayMap = {
          LUNDI: 0,
          MARDI: 1,
          MERCREDI: 2,
          JEUDI: 3,
          VENDREDI: 4,
          SAMEDI: 5,
        };

        const dayOffset = dayMap[event.jour];
        const start = referenceMonday.clone().add(dayOffset, 'days').format('YYYY-MM-DD') + `T${event.heureDebut}`;
        const end = referenceMonday.clone().add(dayOffset, 'days').format('YYYY-MM-DD') + `T${event.heureFin}`;

        return {
          id: event.id,
          title: event.matiere?.nom,
          start,
          end,
          backgroundColor: 'transparent',
          borderColor: 'transparent',
          extendedProps: {
            professeurNames: event.professeurs?.map(p => `${p.prenom} ${p.nom}`).join(', ') || '',
            matiere: event.matiere?.nom,
            matiereColor: event.matiere?.color,
            heureDebut: event.heureDebut,
            heureFin: event.heureFin,
            originalJour: event.jour,
          },
        };
      })
    : [];

  const handleEventDrop = info => {
    const { event } = info;
    const eventId = event.id;
    const newStart = moment(event.start).format('HH:mm');
    const newEnd = moment(event.end).format('HH:mm');
    const newDay = moment(event.start).locale('fr').format('dddd').toUpperCase();

    const overlap = events.some(e => {
      if (e.id === eventId) return false;
      const eStart = moment(e.start);
      const eEnd = moment(e.end);
      return (
        moment(event.start).isBetween(eStart, eEnd, null, '[)') ||
        moment(event.end).isBetween(eStart, eEnd, null, '(]') ||
        (moment(event.start).isSameOrBefore(eStart) && moment(event.end).isSameOrAfter(eEnd))
      );
    });

    if (overlap) {
      message.error('Conflit détecté : cet horaire chevauche un autre cours.');
      info.revert();
      return;
    }

    const updatedEvent = {
      ...listCalendarEntry.find(e => e.id === eventId),
      jour: newDay,
      heureDebut: newStart,
      heureFin: newEnd,
      anneeScolaireId,
      classeId: listCalendarEntry.find(e => e.id === eventId)?.classe.id,
    };

    dispatch(updateCalendarEntry(updatedEvent)).then(response => {
      if (updateCalendarEntry.fulfilled.match(response)) {
        message.success('Cours déplacé avec succès');
      } else {
        message.error('Erreur lors du déplacement');
        info.revert();
      }
    });
  };

  const renderEventContent = eventInfo => {
    const { event } = eventInfo;
    const { professeurNames, matiere, heureDebut, heureFin, matiereColor } = event.extendedProps;

    const formatTime = timeStr => {
      const [hours, minutes] = timeStr.split(':');
      return `${parseInt(hours, 10)}h${minutes}`;
    };

    const handleClick = e => {
      e.stopPropagation();
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
        clickTimeoutRef.current = null;
        return;
      }
      clickTimeoutRef.current = setTimeout(() => {
        onSelect(event.id);
        clickTimeoutRef.current = null;
      }, 250);
    };

    const handleDoubleClick = e => {
      e.stopPropagation();
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
        clickTimeoutRef.current = null;
      }
      handleModal();
    };

    return (
      <Tooltip title={`${matiere} avec ${professeurNames}`}>
        <div
          className="event-card"
          onClick={handleClick}
          onDoubleClick={handleDoubleClick}
          style={{ backgroundColor: matiereColor || '#3788d8' }}
        >
          <div className="event-header">
            <div className="event-title">{matiere}</div>
            {isNotComptable && isNotSurveillantGeneral && !isProf && (
              <div className="event-actions">
                <FontAwesomeIcon
                  icon={faClone}
                  title="Dupliquer le cours"
                  className="action-icon"
                  onClick={e => {
                    e.stopPropagation();
                    onDuplicate(event.id);
                  }}
                />
              </div>
            )}
          </div>

          <div className="event-time-range">
            {isLessThanAnHour(heureDebut, heureFin) ? (
              ''
            ) : (
              <>
                {formatTime(heureDebut)} - {formatTime(heureFin)}
              </>
            )}
          </div>
        </div>
      </Tooltip>
    );
  };

  return (
    <div className="classe-detail-container">
      <Breadcrumb className="custom-breadcrumb">
        {!isProf && (
          <Breadcrumb.Item>
            <Link to={`/home/${currentEcole?.id}`}>{currentEcole?.nom}</Link>
          </Breadcrumb.Item>
        )}
        <Breadcrumb.Item>
          <Link to={`/calendar/${currentEcole?.id}/${anneeScolaireId}`}>Emploi du Temps</Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>Emploi du Temps</Breadcrumb.Item>
      </Breadcrumb>

      <Tabs
        activeKey={activeTab}
        onChange={key => {
          setActiveTab(key);
          const params = new URLSearchParams(props.location.search || '');
          params.set('tab', key);
          props.history.replace({ search: params.toString() });
        }}
        className="custom-tabs"
      >
        <TabPane tab="Emploi du Temps" key="1">
          <ClasseCalendar
            anneeScolaireId={anneeScolaireId}
            events={events}
            loadingCalendar={loadingCalendar}
            classe={classe}
            activeAnneScolaire={activeAnneScolaire}
            isNotComptable={isNotComptable}
            isNotSurveillantGeneral={isNotSurveillantGeneral}
            isProf={isProf}
            isAdmin={isAdmin}
            visibleModal={visibleModal}
            visibleResetModal={visibleResetModal}
            setVisibleModal={setVisibleModal}
            setVisibleResetModal={setVisibleResetModal}
            handleModal={handleModal}
            handleEventDrop={handleEventDrop}
            renderEventContent={renderEventContent}
            resetCalendar={resetCalendar}
            calendarEntryId={calendarEntryId}
            onDelete={onDelete}
          />
        </TabPane>

        <TabPane tab="Évaluations" key="2">
          <ClasseEvaluations ecoleid={ecoleid} classeid={classeid} anneeScolaireId={anneeScolaireId} active={activeTab === '2'} />
        </TabPane>
      </Tabs>
    </div>
  );
};

export default Calendar;
